import { useCallback } from 'react';
import { clusterCountLayerId, clusterLayerId, clusterSourceId, unclusteredLayerId } from "../components/Map/helpers";

const waitForClusterReady = (map, sourceId, callback) => {
  const onData = (e) => {
    if (e.sourceId !== sourceId) return;

    const source = map.getSource(sourceId);
    if (!source) return;

    // Once all tiles and clustering data are ready
    if (map.isSourceLoaded(sourceId)) {
      map.off('sourcedata', onData);
      callback?.(true);
    }
  };

  map.on('sourcedata', onData);
};

const waitForUnclusteredLayer = (map, layerId, callback) => {
  const waitForLayer = () => {
    const features = map.queryRenderedFeatures({ layers: [layerId] });
    if (features.length > 0) {
      callback();
    } else {
      requestAnimationFrame(waitForLayer);
    }
  };
  waitForLayer();
};

export const useClusterUpdater = (
  map,
  poiIdRef,
  updateMarkerVisibility,
) => {

  return useCallback((pois, callback = null, useIdle = false) => {
    if (!map || poiIdRef.current) return;

    const features = pois.map(poi => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [poi.longitude, poi.latitude]
      },
      properties: { ...poi }
    }));
    const data = { type: 'FeatureCollection', features };

    // if source exists, just update data (Mapbox re-clusters automatically)
    if (map.getSource(clusterSourceId)) {
      map.getSource(clusterSourceId).setData(data);
      [clusterLayerId, clusterCountLayerId, unclusteredLayerId].forEach(layerId => {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', 'visible');
        }
      });

      if (callback) {
        if (useIdle) {
          map.once('idle', () => callback(true));
        } else {
          waitForClusterReady(map, clusterSourceId, callback);
        }
      }

    } else {
      // otherwise, add source + layers once
      map.addSource(clusterSourceId, {
        type: 'geojson',
        data,
        cluster: true,
        clusterRadius: 30,
        clusterMaxZoom: 20
      });
      map.addLayer({
        id: clusterLayerId,
        type: 'circle',
        source: clusterSourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': 'rgba(0, 0, 0, 0)',
          'circle-stroke-color': '#000000',
          'circle-stroke-width': 1,
          'circle-radius': ["step", ["get", "point_count"], 22, 100, 24, 750, 26]
        }
      });
      map.addLayer({
        id: clusterCountLayerId,
        type: 'symbol',
        source: clusterSourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        }
      });
      map.addLayer({
        id: unclusteredLayerId,
        type: 'circle',
        source: clusterSourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': 'rgba(0, 0, 0, 0)',
          'circle-radius': 1,
        }
      });

      const onClick = (e) => {
        const feature = e.features[0];
        const clusterId = feature.properties.cluster_id;
        map.getSource(clusterSourceId).getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            map.easeTo({ center: feature.geometry.coordinates, zoom });

            map.once('zoomend', () => {
              waitForUnclusteredLayer(map, unclusteredLayerId, () => {
                updateMarkerVisibility(true);
              });
            });
          }
        );
      };
      map.on('click', clusterLayerId, onClick);

      const onMouseEnter = () => map.getCanvas().style.cursor = 'pointer';
      const onMouseLeave = () => map.getCanvas().style.cursor = '';
      map.on('mouseenter', clusterLayerId, () => onMouseEnter);
      map.on('mouseleave', clusterLayerId, () => onMouseLeave);

      if (callback) map.once('idle', () => callback(true));

      return () => {
        map.off('click', clusterLayerId, onClick);
        map.off('mouseenter', clusterLayerId, onMouseEnter);
        map.off('mouseleave', clusterLayerId, onMouseLeave);
      };
    }
  }, [map, poiIdRef, updateMarkerVisibility]);
};